import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Stack,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TextField,
  Breadcrumbs,
  Link,
  Button,
  InputAdornment,
  TableSortLabel,
  Tooltip,
  Chip,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation } from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import { Resizable } from "re-resizable";
import * as XLSX from "xlsx";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import LoadingSpinner from "../../Components/LoadingSpinner";
import HomeIcon from '@mui/icons-material/Home';


const
  TableComponent = ({
    FiltersJsx,
    columns,
    rows,
    CountryJsx,
    handleRowDoubleClick,
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleClick,
    handleHomeClick,
    isResident,
    handleLocationclick,
    filteredRows,
    title,
    titleMaster,
    handleIconClick,
    isLoading,
    initialSearchTerms = {}, //this props for booking search
  }) => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(1000);
    const location = useLocation();
    const permissions = location.state?.permissions || [];
    const [showFullText, setShowFullText] = useState({});
    const [orderBy, setOrderBy] = useState(columns[0]?.id || '');
    const [order, setOrder] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [localSearchTerms, setLocalSearchTerms] = useState(initialSearchTerms);
    const [initialized, setInitialized] = useState(false);
    const handlePageChange = (newPage) => {
      setPage(newPage);
    };

    // Trigger search change for each initial term based on booking 
    useEffect(() => {
      if (Object.keys(initialSearchTerms).length > 0 && !initialized) {
        setLocalSearchTerms(initialSearchTerms);
        setInitialized(true);


        Object.entries(initialSearchTerms).forEach(([columnId, value]) => {
          if (handleSearchChange) {
            handleSearchChange(columnId, value);
          }
        });
      }
    }, [initialSearchTerms, initialized, handleSearchChange]);
    //close this 
    const handleLocationNavigation = () => {
      handleLocationclick(permissions);
    };

    const handleRequestSort = (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };
    const [columnWidths, setColumnWidths] = useState(() => {

      return columns.reduce((acc, column) => {
        acc[column.id] = 100; // Default width of 200px
        return acc;
      }, {});
    });

    const sortData = (data) => {
      if (!orderBy) return data;

      return [...data].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (order === 'asc') {
          if (typeof aValue === 'number') return aValue - bValue;
          return String(aValue).localeCompare(String(bValue));
        } else {
          if (typeof aValue === 'number') return bValue - aValue;
          return String(bValue).localeCompare(String(aValue));
        }
      });
    };


    const truncateText = (text, maxLength = 20) => {
      if (typeof text !== 'string') return text;
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const handleResize = (column, width) => {
      const newWidth = Math.max(width, 100);
      setColumnWidths(prev => ({
        ...prev,
        [column]: newWidth
      }));
    };
    const sortedAndPaginatedData = sortData(filteredRows)
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

    const handleExportToExcel = async () => {
      setIsExporting(true);
      try {
        const worksheetData = filteredRows.map((row) => {
          let rowData = {};
          columns.forEach((col) => {
            rowData[col.label] = row[col.id] || "";
          });
          return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        const worksheetName = titleMaster
          ? titleMaster.slice(0, 31).replace(/[^a-zA-Z0-9]/g, "_")
          : "TableData";
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = titleMaster
          ? `${titleMaster.replace(/[^a-zA-Z0-9\s]/g, "")}_${currentDate}.xlsx`
          : `TableData_${currentDate}.xlsx`;


        XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);

        await new Promise((resolve) => setTimeout(resolve, 1000));
        XLSX.writeFile(workbook, fileName);

      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setIsExporting(false);
      }
    };

    const DynamicTableCell = ({ column, value }) => {
      const currentWidth = columnWidths[column.id] || 120;
      const shouldShowFullText = currentWidth > 160;
      const getDisplayText = (text) => {
        if (!text || typeof text !== 'string') return text;
        if (shouldShowFullText) return text;
        return text.length > 20 ? `${text.substring(0, 20)}...` : text;
      };

      const displayText = getDisplayText(value);
      const getTextBgColor = () => {
        if (column.id === 'EnqTypeNm') {
          switch (value) {
            case 'Hot': return '#FF0000';
            case 'Warm': return '#FFBF00';
            case 'Booked': return '#01bc5f';
            case 'Refund': return '#4d7499';
            case 'Not interested': return '#FF6347';
            case 'Incorrect Lead': return '#922b21';
            case 'Cold': return '#0000FF';
            case 'Low Budget': return '#148f77';
            case 'Others': return '#FFA500'
            default: return 'transparent';
          }
        } else if (column.id === 'ApprovalFlag') {
          switch (value) {
            case 'Approved': return '#4caf50'; // Green for Approved
            case 'Unapproved': return '#f44336'; // Red for Unapproved
            default: return 'transparent';
          }
        } else if (column.id === 'IsAccGen') {
          switch (value) {
            case 'Generated': return '#80c282ff';
            case 'Pending': return '#e66e66ff';
          }
        }
        return 'transparent';
      };

      const getTextColor = () => {
        if (column.id === 'EnqTypeNm') {
          switch (value) {
            case 'Hot':
            case 'Warm':
            case 'Refund':
            case 'Not interested':
            case 'Low Budget':
            case 'Booked':
            case 'Cold':
            case 'others':
            case 'Incorrect Lead':
            case 'Others': return 'white';
            default: return 'inherit';
          }
        } else if (column.id === 'ApprovalFlag') {
          switch (value) {
            default: return 'inherit';
          }
        } else if (column.id === 'BarcodeFlag') {
          switch (value) {
            case 'Unique': return '#1a4e1cff';
            case 'Lot': return '#d31414ff';
          }
        }
        return 'inherit';
      };

      return (
        <TableCell
          align={column.align}
          sx={{
            width: currentWidth,
            minWidth: currentWidth,
            maxWidth: currentWidth,
            padding: '8px',
            '& .cell-content': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              display: 'block',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <Tooltip
            title={
              <Typography variant="body1" style={{ fontSize: '15px' }}>
                {value}
              </Typography>
            }
            arrow placement="top">
            {/* <div className="cell-content">
              {displayText}
            </div> */}
            <div className="cell-content">
              <span style={{
                backgroundColor: getTextBgColor(),
                color: getTextColor(),
                padding: '2px 6px',
                borderRadius: '4px',
              }}>
                {displayText}
              </span>
            </div>
          </Tooltip>
        </TableCell>
      );
    };

    return (
      <Box
        sx={{
          // maxWidth: "95vw",
          width: { xs: "calc(100vw - 60px)", sm: "95vw", md: "95vw" },
          maxWidth: { xs: "calc(100vw - 60px)", sm: "95vw", md: "95vw" },
          overflowX: "hidden",
          backgroundColor: "#f9f9f9",
          minHeight: "90vh"
        }}
      >
        <ToastContainer />

        <Box
          sx={{
            backgroundColor: "#ffffff",
            padding: { xs: "8px 16px", sm: "12px 16px" },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            borderRadius: "12px",
            margin: "10px auto",
            maxWidth: "95%",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{
              fontSize: { xs: "14px", sm: "16px" },
              display: "flex",
              // flexDirection: { xs: "column", sm: "column", md: "row" }
              flexDirection: { xs: "column", sm: "column", md: "row" },
              justifyContent: { xs: "center", sm: "flex-start", md: "flex-start" },
              alignItems: { xs: "center", sm: "flex-start", md: "flex-start" },
            }}
          >
            <Link
              onClick={handleHomeClick}
              underline="hover"
              color="#635bff"
              sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <Box component="span">Home</Box>
            </Link>

            <Typography
              color="#635bff"
              onClick={handleLocationclick}
              sx={{ cursor: "pointer", fontWeight: "500" }}
            >
              {title}
            </Typography>
            <Typography color="text.primary" sx={{ fontWeight: "500" }}>
              {titleMaster}
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "16px",
              minWidth: "300px",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              marginTop: { xs: "16px", sm: "16px", md: "0px" }
            }}
          >
            {FiltersJsx ? FiltersJsx : null}
            {/* {!isResident && (
              <Button
                variant="contained"
                onClick={handleExportToExcel}
                sx={{ backgroundColor: "#635bff", color: "#ffffff", borderRadius: '20px' }}
              >
                <SaveAltIcon sx={{ fontSize: '18px' }} />
                {isExporting ? "Downloading" : "Export"}
              </Button>
            )} */}
          </Box>
        </Box>

        <Paper
          sx={{
            width: "95%",
            overflow: "hidden",
            margin: "10px auto",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            px: "15px",
            py: '15px',
          }}
        >
          {isLoading ? (
            <Box sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              textAlign: "center",
              zIndex: 1000
            }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 450 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow sx={{ "& > th": { padding: "12px" } }}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            backgroundColor: "#dddbff",
                            color: "#333",
                            fontWeight: "600",
                            padding: "4px 2px 6px 5px !important",
                            border: "0.5px solid #ddd",
                            borderBottom: "2px solid #ddd",
                            width: column.width || 'auto',

                          }}
                        >
                          <Resizable
                            size={{
                              width: columnWidths[column.id] || 120,
                              height: "100%"
                            }}
                            onResizeStop={(e, direction, ref, d) => {
                              handleResize(
                                column.id,
                                (columnWidths[column.id] || 120) + d.width
                              );
                            }}
                            enable={{ right: true }}
                            minWidth={120}
                            handleStyles={{
                              right: {
                                width: '8px',
                                right: '-4px',
                                cursor: 'col-resize'
                              }
                            }}
                          >
                            <Box>
                              <TableSortLabel
                                // active={true}
                                active={orderBy === column.id}
                                direction={orderBy === column.id ? order : 'asc'}
                                onClick={() => handleRequestSort(column.id)}
                                sx={{
                                  '& .MuiTableSortLabel-icon': {
                                    opacity: '1 !important',
                                    visibility: 'visible !important'
                                  }
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight="500">
                                  {column.label}
                                </Typography>
                              </TableSortLabel>

                              <TextField
                                size="small"
                                variant="outlined"
                                onChan
                                placeholder={`Search ${column.label}`}
                                // onChange={(e) => handleSearchChange(column.id, e.target.value)}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setLocalSearchTerms(prev => ({ ...prev, [column.id]: newValue }));
                                  if (handleSearchChange) {
                                    handleSearchChange(column.id, newValue);
                                  }
                                }}
                                sx={{
                                  mt: 1,
                                  width: '100%',
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    backgroundColor: "#f9f9f9",
                                    fontSize: "0.75rem",
                                    height: "26px",
                                  },
                                }}
                              />
                            </Box>
                          </Resizable>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndPaginatedData.map((row, index) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index}
                        onDoubleClick={() => handleRowDoubleClick(row)}
                        sx={{
                          "& > td": { padding: "3px", border: "0.5px solid #ddd" },
                          "&:hover": { backgroundColor: "#f1f1f1", cursor: "pointer" },
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        {CountryJsx && <CountryJsx row={row} ind={index} />}
                        {(CountryJsx ? columns.slice(1) : columns).map((column) => (
                          <DynamicTableCell
                            key={column.id}
                            column={column}
                            value={column.id === "Index" ? (index + 1) : row[column.id]}
                          />
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[15, 50, 100, 500, 1000, 2000, 5000]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                handlePageChange={handlePageChange}
                onPageChange={(event, newPage) => handlePageChange(newPage)}
                onRowsPerPageChange={(event) => {
                  const newRowsPerPage = parseInt(event.target.value, 10);
                  setRowsPerPage(newRowsPerPage);
                  setPage(0);
                }}
                sx={{
                  backgroundColor: "#dddbff",
                  borderTop: "1px solid #ddd",
                  color: "#333",
                  borderRadius: "0 0 12px 12px",
                  "& .MuiToolbar-root": {
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "100%",

                    flexWrap: "wrap",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    display: "none",
                  },
                  "& .MuiTablePagination-actions": {
                    display: "none",
                  },

                  "& .MuiTablePagination-selectLabel": {
                    marginBottom: 2,
                  },
                  "& .MuiTablePagination-select": {
                    marginBottom: 0,
                  },
                }}
                ActionsComponent={({ onPageChange }) => (
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      justifyContent: "center",
                      marginLeft: "16px",
                    }}
                  >
                    {/* Total Records */}
                    <Typography
                      variant="body2"
                      sx={{ position: 'absolute', left: '20px', fontWeight: 'bold', color: "#333", }}
                    >
                      Total Records: {filteredRows.length}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={page + 1}
                      onChange={(event, newPage) => handlePageChange(newPage - 1)}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "#635bff",
                          color: "#ffffff",
                        },
                      }}
                    />
                  </Stack>
                )}
              />
            </>
          )}
        </Paper>
      </Box>
    );
  };

export default TableComponent;